import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getEnvelopeDocumentCheckStorage,
  modifyDocumentRequestFormOrders,
  modifyEnvelopeSignDocument,
  removeEnvelopeSignDocument,
} from "../../../../services/CommonService";
import Utils from "../../../../utils";
import { toast } from "react-toastify";
import MakeFillablePopup from "./MakeFillablePopup";
import ContractSummaryModal from "./ContractSummaryModal";
import { useNegotiation } from "../../../../context/NegotiationProvider";
import { CustomSelect, CustomDatePicker } from "../../../../components";
import "../../../../assets/css/sleeve.css";
import { 
  saveFilesToIndexedDB, 
  loadFilesFromIndexedDB, 
  deleteFilesFromIndexedDB 
} from "../../../../utils/indexedDBHelper";

function EnvelopeStep3(props) {
  const { negotiationData } = useNegotiation();
  const navigate = useNavigate();

  // Core state
  const [directMakeFillableOpen, setDirectMakeFillableOpen] = useState(false);


  // Document form state
  const [docId, setDocId] = useState(0);
  const [docName, setDocName] = useState("");
  const [docDetail, setDocDetail] = useState("");
  const [documentsList, setDocumentsList] = useState([]);
  const [fileKey, setFileKey] = useState(Date.now());
  const [removeDocIds, setRemoveDocIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [mode, setMode] = useState(1);
  const [showForm, setShowForm] = useState(true);
  const [editDocId, setEditDocId] = useState(null);

  // Contract fields
  const [contractType, setContractType] = useState("");
  const [contractTerm, setContractTerm] = useState("Definite");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [terminationNoticePeriod, setTerminationNoticePeriod] = useState("");
  const [isRenewable, setIsRenewable] = useState(false);

  // NDA specific fields
  const [purposeOfDisclosure, setPurposeOfDisclosure] = useState("");
  const [durationInMonths, setDurationInMonths] = useState("");
  const [confidentialInfoDefinition, setConfidentialInfoDefinition] = useState("");
  const [exclusions, setExclusions] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");

  // Negotiation PDF URL
  const [negotiationPdfUrl, setNegotiationPdfUrl] = useState(null);

  // Refs
  const fileInputRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const btnSignDocumentPopup = useRef(null);
  const docDragItem = useRef();
  const docDragOverItem = useRef();

  // Make fillable state
  const [makeFillable, setMakeFillable] = useState(false);
  const [signatureDocumentData, setSignatureDocumentData] = useState(null);

  // Errors
  const errorsObj = {
    doc_name: "",
    doc_detail: "",
    doc_file: "",
    contract_type: "",
    contract_term: "",
    start_date: "",
    end_date: "",
    termination_notice_period: "",
    purpose_of_disclosure: "",
    duration_in_months: "",
    confidential_info_definition: "",
    exclusions: "",
    jurisdiction: "",
  };
  const [errors, setErrors] = useState(errorsObj);

  useEffect(() => {
    if (props.is_negotiation) {
      const storedUrl = localStorage.getItem("negotiation_pdf_url");
      if (storedUrl) setNegotiationPdfUrl(storedUrl);
    }
  }, [props.is_negotiation]);


  const recipientCount = props.formObj.recipient_List?.length ?? 0;
  const isGroupSigningDisabled = recipientCount <= 1;

  useEffect(() => {
    if (isGroupSigningDisabled && mode === 2) {
      setMode(1);
    }
  }, [isGroupSigningDisabled, mode]);

  useEffect(() => {
    const { pdfUrl } = negotiationData || {};

    if (pdfUrl) {
      setNegotiationPdfUrl(pdfUrl);
      localStorage.setItem("negotiation_pdf_url", pdfUrl);
      console.log("Negotiation PDF URL (from context):", pdfUrl);
    } else {
      const storedUrl = localStorage.getItem("negotiation_pdf_url");
      if (storedUrl) {
        setNegotiationPdfUrl(storedUrl);
        console.log("Negotiation PDF URL (from localStorage):", storedUrl);
      }
    }
  }, [negotiationData]);

  const getStorageKey = () => {
    if (!props.formObj?.envelope_id) return null;
    return `envelope_step3_draft_${props.formObj.envelope_id}`;
  };

  // Save draft on change (VIEW STATE ONLY)
  useEffect(() => {
    const key = getStorageKey();
    if (!key) return;

    if (showForm) {
      const viewState = {
        showForm,
        mode,
        editDocId,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(viewState));
    }
  }, [showForm, mode, editDocId, props.formObj?.envelope_id]);

  // Restore draft on mount
  useEffect(() => {
    const key = getStorageKey();
    if (!key) return;

    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        
        if (data.showForm) {
          setShowForm(true);
          if (data.mode) setMode(data.mode);
          
          // If we were editing a doc, try to restore that state from props
          if (data.editDocId) {
            setEditDocId(data.editDocId);
            const foundDoc = props.formObj?.sign_documents?.find(d => d.id === data.editDocId);
            if (foundDoc) {
               setDocId(foundDoc.id);
               setDocName(foundDoc.name || foundDoc.file_name);
               setDocDetail(foundDoc.doc_detail || "");
               
               let filesToDisplay = [];
               if (foundDoc.documents && foundDoc.documents.length > 0) {
                 filesToDisplay = foundDoc.documents.map((file) => ({
                   id: file.id,
                   name: file.file_name || file.name,
                   kb: file.file_size || "0",
                   path: file.path || file.sign_file_path,
                   file: null,
                 }));
               } else if (foundDoc.file_name && foundDoc.path) {
                 filesToDisplay = [{
                   id: foundDoc.id,
                   name: foundDoc.file_name,
                   kb: "0",
                   path: foundDoc.path,
                   file: null,
                 }];
               }
               setDocumentsList(filesToDisplay);
               
               setContractType(foundDoc.contract_type);
               setContractTerm(foundDoc.contract_term);
               setStartDate(foundDoc.start_date ? foundDoc.start_date.split("T")[0] : "");
               setEndDate(foundDoc.end_date ? foundDoc.end_date.split("T")[0] : "");
               setTerminationNoticePeriod(foundDoc.termination_notice_period || "");
               setIsRenewable(foundDoc.is_renewable === "Yes" || foundDoc.is_renewable === true);
               setPurposeOfDisclosure(foundDoc.purpose_of_disclosure || "");
               setDurationInMonths(foundDoc.duration_in_months || "");
               setConfidentialInfoDefinition(foundDoc.confidential_info_definition || "");
               setExclusions(foundDoc.exclusions || "");
               setJurisdiction(foundDoc.jurisdiction || "");
            }
          }
        }
        
      } catch (e) {
        console.error("Failed to parse view state", e);
      }
    }
  }, [props.formObj?.envelope_id]);

  const processFiles = (files) => {
    let newFiles = [];
    let hasNonPdf = false;
    let hasOversizedFile = false;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) continue;

      if (file instanceof File) {
        const isPdf =
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf");
        const isWithinSizeLimit = file.size <= 8 * 1024 * 1024;

        if (!isPdf) {
          hasNonPdf = true;
          continue;
        }

        if (!isWithinSizeLimit) {
          hasOversizedFile = true;
          continue;
        }

        let kb = file.size / 1024;
        newFiles.push({
          id: 0,
          name: file.name,
          kb: parseFloat(kb).toFixed(2),
          file: file,
        });
      }
    }

    if (hasNonPdf) toast.error("Only PDF files are allowed!");
    if (hasOversizedFile) toast.error("Maximum file size is 8MB");

    if (newFiles.length > 0) {
      setDocumentsList((prev) => {
        const updatedList = [...prev, ...newFiles].filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.name === item.name && t.id === item.id)
        );
        return updatedList;
      });
      setFileKey(Date.now());
    }
  };

  const onDocFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      processFiles(newFiles);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Drag handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) processFiles(Array.from(files));
  };

  const onRemoveDoc = (e, obj) => {
    e.preventDefault();
    setDocumentsList((current) =>
      current.filter((doc) => doc.name !== obj.name)
    );
    if (obj.id > 0) setRemoveDocIds((prev) => [...prev, obj.id]);
  };

  const clearDocumentForm = () => {
    setDocId(0);
    setDocName("");
    setDocDetail("");
    setDocumentsList([]);
    setMode(1);
    setRemoveDocIds([]);
    setFileKey(Date.now());
    setErrors(errorsObj);
    setContractType("");
    setContractTerm("Definite");
    setStartDate("");
    setEndDate("");
    setTerminationNoticePeriod("");
    setIsRenewable(false);
    setEditDocId(null);
    setShowForm(false);
    setPurposeOfDisclosure("");
    setDurationInMonths("");
    setConfidentialInfoDefinition("");
    setExclusions("");
    setJurisdiction("");
    
    // Clear draft from storage
    const key = getStorageKey();
    if (key) localStorage.removeItem(key);
  };

  const handleModeChange = (val) => {
    setMode(val);
  };

  async function onSaveNegotiationDoc(e) {
    e.preventDefault();

    let error = false;
    const errorObj = { ...errors };
    const envelopeFormData = { ...props.formObj };

    // 🧠 Validation
    if (!negotiationPdfUrl && documentsList.length === 0) {
      errorObj.doc_file = "Please upload at least one document";
      error = true;
    }

    if (documentsList.length > 10) {
      toast.error("Maximum 10 files allowed");
      error = true;
    }

    setErrors(errorObj);
    if (error) return;

    props.setLoading(true);

    try {
      // 🧾 Prepare FormData
      const formData = new FormData();
      formData.append("envelope_id", envelopeFormData.envelope_id);
      formData.append("mode", mode);
      formData.append("doc_id", editDocId || docId);

      const cleanEnvelopeName = envelopeFormData?.envelope_name_updated || envelopeFormData?.envelope_name || "Negotiation Document";

      const envelopeName = Utils.getEnvelopeNameForPayload(
        cleanEnvelopeName,
        envelopeFormData?.is_negotiation
      );

      // 🧩 Add basic metadata
      formData.append("name", envelopeName);
      formData.append("detail", docDetail || `Negotiation file created under ${envelopeName}`);
      formData.append("is_negotiation", true);

      // 🧩 Case 1: Negotiation context URL
      if (negotiationPdfUrl) {
        formData.append("doc_link", JSON.stringify([negotiationPdfUrl]));
      }
      // 🧩 Case 2: Locally uploaded files
      else {
        (documentsList || []).forEach((doc, index) => {
          if (doc.file) {
            formData.append(`doc_file[${index}]`, doc.file);
          }
          formData.append(`doc_order[${index}]`, index);
        });

        if (removeDocIds.length > 0) {
          formData.append("remove_doc_id", JSON.stringify(removeDocIds));
        }
      }

      // 🚀 API Call
      const response = await modifyEnvelopeSignDocument(formData);
      const data = response?.data?.data;

      const obj = {
        mode,
        id: data?.id,
        name: envelopeName,
        doc_detail: docDetail || "",
        path: data?.path,
        request_display: [],
        request_id: null,
        dimensions: data?.dimensions,
        pdf_link: negotiationPdfUrl || null,
      };


      // 🧩 Update parent envelope form object
      if (editDocId) {
        const index = envelopeFormData.sign_documents.findIndex(
          (doc) => doc.id === editDocId
        );
        if (index >= 0) envelopeFormData.sign_documents[index] = obj;
      } else {
        envelopeFormData.sign_documents.push(obj);
      }

      props.setFormObj(envelopeFormData);

      // ✅ Success message
      toast.success(response?.data?.message || "Negotiation document saved successfully");

      // 🟢 Auto-open MakeFillablePopup (same as normal contract flow)
      setSignatureDocumentData(obj);
      setDirectMakeFillableOpen(true);

      // 🔄 Reset form
      clearDocumentForm();
    } catch (err) {
      console.error("Error saving negotiation document:", err);
      toast.error("Failed to save negotiation document");
    } finally {
      props.setLoading(false);
    }
  }

  // Full contract save handler
  // Full contract save handler
  async function onSaveDoc(e) {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errors };
    const envelopeFormData = { ...props.formObj };
    let totalKb = 0;

    if (documentsList.length === 0) {
      errorObj.doc_file = "Please upload at least one document";
      error = true;
    }

    if (!contractType) {
      errorObj.contract_type = "Contract type is required";
      error = true;
    }

    if (!startDate) {
      errorObj.start_date = "Start date is required";
      error = true;
    }

    // ... (Validation logic same as before) ...
    // Validation code hidden for brevity (Contract Term, NDA fields, etc.)

    // Size Validation
    if (documentsList.length > 10) {
      toast.error("Maximum 10 files allowed");
      error = true;
    } else if (documentsList.length > 0) {
      for (let i = 0; i < documentsList.length; i++) {
        totalKb += parseFloat(documentsList[i]["kb"]);
      }
      if (totalKb > 0) {
        let totalMb = totalKb / 1024;
        if (totalMb > 8) {
          toast.error("Maximum 8 MB total file size allowed");
          error = true;
        }
      }
    }

    setErrors(errorObj);
    if (error) return;

    props.setLoading(true);
    const formData = new FormData();
    formData.append("envelope_id", envelopeFormData.envelope_id);
    formData.append("mode", mode);
    formData.append("doc_id", editDocId || docId);
    formData.append("name", docName);
    formData.append("detail", docDetail);
    formData.append("contract_type", contractType);

    if (contractTerm) formData.append("contract_term", contractTerm);
    if (startDate) formData.append("start_date", startDate);
    if (endDate) formData.append("end_date", endDate);
    if (terminationNoticePeriod) formData.append("termination_notice_period", terminationNoticePeriod);
    
    // Always send is_renewable as it's a boolean toggle (Yes/No)
    formData.append("is_renewable", isRenewable ? "Yes" : "No");

    // NDA Fields - Only append if filled
    if (purposeOfDisclosure) formData.append("purpose_of_disclosure", purposeOfDisclosure);
    if (durationInMonths) formData.append("duration_in_months", durationInMonths);
    if (confidentialInfoDefinition) formData.append("confidential_info_definition", confidentialInfoDefinition);
    if (exclusions) formData.append("exclusions", exclusions);
    if (jurisdiction) formData.append("jurisdiction", jurisdiction);

    documentsList.forEach((doc, index) => {
      if (doc.file) {
        // New file - send the actual file
        formData.append(`doc_file[${index}]`, doc.file);
      } else if (doc.id && doc.id > 0) {
        // Existing file - send the ID so server knows to keep it
        formData.append(`existing_doc_id[${index}]`, doc.id);
      }
      formData.append(`doc_order[${index}]`, index);
    });


    if (removeDocIds.length > 0) {
      formData.append("remove_doc_id", JSON.stringify(removeDocIds));
    }

    let isCall = true;
    if (totalKb > 0) {
      isCall = false;
      try {
        const res = await getEnvelopeDocumentCheckStorage({ kb: totalKb });
        if (res.data.is_storage_full === false) isCall = true;
        else {
          toast.error(res.data.message);
          props.setLoading(false);
          return;
        }
      } catch (err) {
        props.setLoading(false);
        toast.error(Utils.getErrorMessage(err));
        return;
      }
    }

    if (!isCall) return;

    // With IndexedDB, files loaded for editing now have doc.file (actual File objects)
    // so we can proceed with normal API call for re-merging
    const hasFilesToUpload = documentsList.some(doc => doc.file !== null);
    
    // If editing without new File objects, send existing paths as doc_link
    // This allows form field updates (contract type, dates, etc.) without re-uploading
    if (editDocId && !hasFilesToUpload) {
      console.log("Edit mode with existing files - sending paths as doc_link for metadata update");
      
      // Get existing file paths
      const existingPaths = documentsList
        .filter(doc => doc.path)
        .map(doc => doc.path);
      
      if (existingPaths.length > 0) {
        formData.append("doc_link", JSON.stringify(existingPaths));
      }
    }

    try {
      const response = await modifyEnvelopeSignDocument(formData);

      // Save documents to IndexedDB (File objects persist) AND localStorage (metadata backup)
      const docIdForStorage = editDocId || response.data.data.id;
      console.log("Saving documents to IndexedDB with key:", docIdForStorage);
      console.log("Documents being saved:", documentsList);
      
      // Save to IndexedDB (stores actual File objects)
      await saveFilesToIndexedDB(docIdForStorage, documentsList);
      
      // Also save metadata to localStorage as backup
      localStorage.setItem(
        `documents_${docIdForStorage}`,
        JSON.stringify(documentsList.map(doc => ({
          id: doc.id,
          name: doc.name,
          kb: doc.kb,
          path: doc.path
        })))
      );

      clearDocumentForm();
      let fileName = response.data.data.path.split("/").pop();

      let obj = {
        mode: mode,
        doc_detail: docDetail,
        file_name: fileName,
        id: response.data.data.id,
        name: docName,
        orders: 0,
        path: response.data.data.path,
        request_display: [],
        request_id: null,
        dimensions: response.data.data.dimensions,
        contract_type: contractType,
        contract_term: contractTerm || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        termination_notice_period: terminationNoticePeriod || undefined,
        is_renewable: isRenewable,
        purpose_of_disclosure: purposeOfDisclosure || undefined,
        duration_in_months: durationInMonths || undefined,
        confidential_info_definition: confidentialInfoDefinition || undefined,
        exclusions: exclusions || undefined,
        jurisdiction: jurisdiction || undefined,
      };

      if (editDocId) {
        const index = envelopeFormData.sign_documents.findIndex(
          (doc) => doc.id === editDocId
        );
        envelopeFormData.sign_documents[index] = obj;
      } else {
        envelopeFormData.sign_documents.push(obj);
      }

      props.setFormObj(envelopeFormData);
      setDirectMakeFillableOpen(true);
      setSignatureDocumentData(obj);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      props.setLoading(false);
    }
  }

  const onDocumentEdit = async (e, data) => {
    e.preventDefault();
    setEditDocId(data.id);
    setDocId(data.id);
    setDocName(data.name || data.file_name);
    setDocDetail(data.doc_detail || "");

    let filesToDisplay = [];

    // PRIORITY 1: Try to get documents from IndexedDB (has actual File objects)
    try {
      const indexedDBFiles = await loadFilesFromIndexedDB(data.id);
      if (indexedDBFiles && indexedDBFiles.length > 0) {
        filesToDisplay = indexedDBFiles;
        console.log("Files loaded from IndexedDB with File objects:", filesToDisplay);
        // NOTE: Don't delete IndexedDB here - will be overwritten on successful save
      }
    } catch (err) {
      console.error("Error loading from IndexedDB:", err);
    }

    // PRIORITY 2: Fallback to localStorage (metadata only, no File objects)
    if (filesToDisplay.length === 0) {
      const storageKey = `documents_${data.id}`;
      const storedDocuments = localStorage.getItem(storageKey);
      console.log("Fallback to localStorage, key:", storageKey);
      
      if (storedDocuments) {
        try {
          const parsedDocs = JSON.parse(storedDocuments);
          if (Array.isArray(parsedDocs) && parsedDocs.length > 0) {
            filesToDisplay = parsedDocs.map((doc) => ({
              id: doc.id || 0,
              name: doc.name,
              kb: doc.kb || "0",
              path: doc.path || null,
              file: null,
            }));
            console.log("Files from localStorage (no File objects):", filesToDisplay);
          }
        } catch (err) {
          console.error("Failed to parse localStorage documents", err);
        }
        // NOTE: Don't delete localStorage here - will be overwritten on successful save
      }
    }

    // PRIORITY 3: Fallback to server data
    if (filesToDisplay.length === 0) {
      if (data.documents && data.documents.length > 0) {
        filesToDisplay = data.documents.map((file) => ({
          id: file.id,
          name: file.file_name || file.name,
          kb: file.file_size || "0",
          path: file.path || file.sign_file_path,
          file: null,
        }));
      }
      else if (data.file_name && data.path) {
        filesToDisplay = [{
          id: data.id,
          name: data.file_name,
          kb: "0",
          path: data.path,
          file: null,
        }];
      }
    }

    setDocumentsList(filesToDisplay);

    setContractType(data.contract_type);
    setContractTerm(data.contract_term);

    setStartDate(data.start_date ? data.start_date.split("T")[0] : "");
    setEndDate(data.end_date ? data.end_date.split("T")[0] : "");

    setTerminationNoticePeriod(data.termination_notice_period || "");
    setIsRenewable(data.is_renewable === "Yes" || data.is_renewable === true);
    setMode(data.mode ? parseInt(data.mode) : 1);
    setPurposeOfDisclosure(data.purpose_of_disclosure || "");
    setDurationInMonths(data.duration_in_months || "");
    setConfidentialInfoDefinition(data.confidential_info_definition || "");
    setExclusions(data.exclusions || "");
    setJurisdiction(data.jurisdiction || "");

    setShowForm(true);
  };

  const handleDocDragStart = (e, position) => {
    docDragItem.current = position;
  };

  const handleDocDragEnter = (e, position) => {
    docDragOverItem.current = position;
  };

  const handleDocDrop = (e) => {
    const copyListItems = [...documentsList];
    const dragItemContent = copyListItems[docDragItem.current];
    copyListItems.splice(docDragItem.current, 1);
    copyListItems.splice(docDragOverItem.current, 0, dragItemContent);
    docDragItem.current = null;
    docDragOverItem.current = null;
    setDocumentsList(copyListItems);
  };


  function onBack(e) {
    e.preventDefault();

    if (props.is_negotiation === true) {
      return navigate("/manage/negotiation");
    }

    const formData = { ...props.formObj };
    formData.current_step = 2;
    props.setFormObj(formData);
    navigate("/client-portal/sleeve/edit/" + formData.envelope_uuid + "/2");
  }

  function onSaveExit(e) {
    e.preventDefault();
    onSubmitForm(1);
  }

  function onSave(e) {
    e.preventDefault();
    onSubmitForm(2);
  }

  function onSubmitForm(type) {
    const envelopeFormData = { ...props.formObj };
    if (
      envelopeFormData.documents.length === 0 &&
      envelopeFormData.fill_forms.length === 0 &&
      envelopeFormData.sign_documents.length === 0
    ) {
      toast.error("Please save Document");
    } else {
      if (envelopeFormData.sign_documents.length > 0) {
        let isAllFillable = true;

        for (let i = 0; i < envelopeFormData.sign_documents.length; i++) {
          const doc = envelopeFormData.sign_documents[i];

          if (doc.request_id === null || (doc.request_display?.length ?? 0) === 0) {
            isAllFillable = false;
            break;
          }
        }

        if (!isAllFillable) {
          toast.error("Please make fillable sign request.");
          return false;
        }
      }


      let obj = {
        id: envelopeFormData.envelope_id,
        document: envelopeFormData.documents,
        request_form: envelopeFormData.fill_forms,
        sign_document: envelopeFormData.sign_documents,
      };

      props.setLoading(true);
      modifyDocumentRequestFormOrders(obj)
        .then((response) => {
          if (type === 1) {
            navigate("/manage/draft");
          } else {
            envelopeFormData.current_step = 4;
            props.setFormObj(envelopeFormData);
            navigate(
              "/client-portal/sleeve/edit/" +
              envelopeFormData.envelope_uuid +
              "/4"
            );
          }
          toast.success(response.data.message);
          props.setLoading(false);
        })
        .catch((err) => {
          props.setLoading(false);
          toast.error(Utils.getErrorMessage(err));
        });
    }
  }

  const handleDownload = async (e, obj) => {
    e.preventDefault();
    props.setLoading(true);
    await Utils.downloadAnyFile(obj.path, obj.file_name);
    props.setLoading(false);
  };

  // const onRequestSignDocumentDelete = (e, data) => {
  //   e.preventDefault();
  //   props.setLoading(true);
  //   let obj = {
  //     id: data.id,
  //   };
  //   removeEnvelopeSignDocument(obj)
  //     .then((response) => {
  //       let envelopeData = { ...props.formObj };
  //       let index = envelopeData.sign_documents.findIndex(
  //         (x) => x.id === obj.id
  //       );
  //       envelopeData.sign_documents.splice(index, 1);
  //       props.setFormObj(envelopeData);
  //       toast.success(response.data.message);
  //       props.setLoading(false);
  //     })
  //     .catch((err) => {
  //       toast.error(Utils.getErrorMessage(err));
  //       props.setLoading(false);
  //     });
  // };

  const onRequestSignDocumentRender = async (e, data) => {
    e.preventDefault();
    const keysToRemove = ["mousePosition", "pagesPosition", "placeholders"];
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    props.setLoading(true);
    await setSignatureDocumentData(data);
    await setMakeFillable(true);
    btnSignDocumentPopup.current?.click();
  };

  const handleMakeFillable = () => {
    let current = makeFillable === false ? true : false;
    setMakeFillable(current);
  };

  useEffect(() => {
    setDocName(props.formObj.envelope_name_updated || props.formObj.envelope_name || "");
  }, [props.formObj]);


  useEffect(() => {
    if (directMakeFillableOpen) {
      const keysToRemove = ["mousePosition", "pagesPosition", "placeholders"];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      setMakeFillable(true);
      btnSignDocumentPopup.current?.click();
      setDirectMakeFillableOpen(false);
    }
  }, [directMakeFillableOpen]);

  useEffect(() => {
    if (props.formObj && props.formObj.sign_documents.length > 0) {
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  }, [props.formObj]);

  useEffect(() => {
    if (contractTerm === "Definite") {
      setIsRenewable(false);
    }
  }, [contractTerm]);

  // Render Negotiation Form (Simplified)
  const renderNegotiationForm = () => {
    return (
      <div className="sleeve-gradient-bg sleeve-step">
        <div className="sleeve-main-card">
          {/* Custom Header */}
          <div className="sleeve-contract-setup-header">
            <div className="sleeve-contract-setup-header-content">
              <div>
                <div className="sleeve-contract-title">Contract Setup</div>
                <div className="sleeve-contract-subtitle">Review and configure your negotiation document.</div>
              </div>
              <div className="text-end">
                {!props.is_negotiation && <div className="sleeve-step-indicator-custom">Step 3 of 4</div>}
              </div>
            </div>
          </div>

          <div className="py-2">
            {/* Signing Mode Section */}
            <div className="sleeve-sender-card mb-4">
              <h2 className="sleeve-heading mb-2">
                <span>
                  Signing Mode
                  <i
                    className="bi bi-info-circle ms-1 text-muted cursor-pointer fs-6"
                    aria-hidden="true"
                    data-toggle="tooltip"
                    data-placement="right"
                    title="Choose how the recipients will sign the document."
                  />
                </span>
              </h2>

              <div className="row g-4 mt-2">
                {[1, 2].map((modeVal) => {
                  const isDisabled = modeVal === 2 && isGroupSigningDisabled;

                  return (
                    <div
                      key={modeVal}
                      className="col-md-6"
                      title={
                        isDisabled
                          ? "Group Signing requires two or more recipients."
                          : modeVal === 1
                            ? "Each Collaborator will receive a separate copy of the contract to sign individually."
                            : "All Collaborators will sign the same contract together."
                      }
                    >
                      <div
                        className={`option-card card h-100 border shadow-sm ${mode === modeVal ? "active" : ""
                          } ${isDisabled ? "disabled-option" : ""}`}
                        onClick={() => !isDisabled && handleModeChange(modeVal)}
                        style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                      >
                        <div className="card-body d-flex align-items-center gap-3">
                          <input
                            type="radio"
                            name="mode"
                            id={`mode${modeVal}`}
                            value={modeVal}
                            checked={mode === modeVal}
                            onChange={(e) =>
                              !isDisabled &&
                              handleModeChange(parseInt(e.target.value))
                            }
                            className="form-check-input"
                            disabled={isDisabled}
                          />
                          <div className="flex-grow-1">
                            <label
                              htmlFor={`mode${modeVal}`}
                              className="fw-bold mb-1"
                              style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                            >
                              {modeVal === 1 ? "Individual Signing" : "Group Signing"}
                            </label>
                            <p className="text-muted small mb-0">
                              {modeVal === 1
                                ? "Each recipient signs separately"
                                : "Multiple recipients sign together"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* File Upload Section - Only show if no PDF URL from negotiation */}
            {!negotiationPdfUrl && (
              <div className="sleeve-sender-card mb-4">
                <h5 className="sleeve-heading mb-3">
                  <span>
                    <i className="bi bi-cloud-arrow-up me-2"></i>
                    Document Upload
                  </span>
                </h5>
                <div className="p-2">
                  {/* Upload Dropzone */}
                  <div
                    className={`step2-empty-state ${isDragging ? "bg-white" : ""}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      cursor: "pointer",
                      borderColor: isDragging ? "#0d6efd" : "#ced4da",
                      borderWidth: "2px"
                    }}
                  >
                    <i className="bi bi-cloud-upload fs-1 text-primary mb-3"></i>
                    <p className="fw-bold mb-1">Drop files here or click to upload</p>
                    <small className="text-muted">PDF only, max 8MB</small>
                    <input
                      type="file"
                      ref={fileInputRef}
                      key={fileKey}
                      onChange={onDocFile}
                      accept="application/pdf"
                      className="d-none"
                      multiple
                    />
                  </div>

                  {/* Error */}
                  {errors.doc_file && (
                    <div className="text-danger small mt-2">{errors.doc_file}</div>
                  )}

                  <p className="mt-3 text-muted small">
                    * You can reorder your uploaded documents by dragging
                  </p>

                  {/* Uploaded Files List */}
                  {documentsList.length > 0 && (
                    <div className="uploaded-files mt-3">
                      {documentsList.map((item, index) => (
                        <div
                          key={index}
                          className="file-item d-flex align-items-center justify-content-between border rounded-3 p-3 mb-2 bg-light shadow-sm"
                          draggable
                          onDragStart={(e) => handleDocDragStart(e, index)}
                          onDragEnter={(e) => handleDocDragEnter(e, index)}
                          onDragEnd={handleDocDrop}
                          onDragOver={(e) => e.preventDefault()}
                          style={{ cursor: "grab" }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="file-index badge bg-primary"
                              style={{
                                borderRadius: "50%",
                                height: "30px",
                                width: "30px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {index + 1}
                            </span>
                            <span className="file-name fw-semibold">{item.name}</span>
                            {!item.file && (
                              <span className="badge bg-secondary ms-2">Existing</span>
                            )}
                          </div>
                          <div className="file-actions d-flex align-items-center gap-3">
                            {item.path && (
                              <i
                                className="bi bi-download text-success fs-5"
                                title="Download"
                                onClick={(e) => handleDownload(e, item)}
                                style={{ cursor: "pointer" }}
                              />
                            )}
                            <i
                              className="bi bi-x text-danger fs-5"
                              title="Remove"
                              onClick={(event) => onRemoveDoc(event, item)}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="sleeve-step-buttons">
              <button className="sleeve-btn-back" onClick={onBack}>
                Back
              </button>
              <div className="sleeve-step-buttons-right">
                <button
                  className="sleeve-btn-back me-2"
                  onClick={clearDocumentForm}
                >
                  Cancel
                </button>
                <button
                  className="sleeve-btn-next"
                  onClick={onSaveNegotiationDoc}
                  disabled={props.loading}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Full Contract Form (keeping original logic for non-negotiation)
  const renderFullContractForm = () => {
    return (
      <div className="sleeve-gradient-bg sleeve-step">
        <div className="sleeve-main-card">
          {/* Custom Header */}
          <div className="sleeve-contract-setup-header">
            <div className="sleeve-contract-setup-header-content">
              <div>
                <div className="sleeve-contract-title">Contract Setup</div>
                <div className="sleeve-contract-subtitle">Define the terms and details of your contract.</div>
              </div>
              <div className="text-end">
                {!props.is_negotiation && <div className="sleeve-step-indicator-custom">Step 3 of 4</div>}
              </div>
            </div>
          </div>

          <div className="py-2">
            {/* Signing Mode Section */}
            <div className="sleeve-sender-card mb-4">
              <h5 className="sleeve-heading mb-2">
                <span>
                  Signing Mode
                  <i
                    className="bi bi-info-circle ms-1 text-muted cursor-pointer fs-6"
                    aria-hidden="true"
                    data-toggle="tooltip"
                    data-placement="right"
                    title="Choose how the recipients will sign the document."
                  />
                </span>
              </h5>

              <div className="row g-4 mt-2">
                {[
                  {
                    mode: 1,
                    title: "Individual Signing",
                    description: "Each recipient signs separately",
                    tooltip:
                      "Each recipient will receive a separate copy of the contract to sign individually. Signatures will not be visible to other recipients.",
                  },
                  {
                    mode: 2,
                    title: "Group Signing",
                    description: "Multiple recipients sign together",
                    tooltip:
                      "All recipients will sign the same contract. Everyone's signatures will appear together in a single copy.",
                  },
                ].map((modeOption) => {
                  const isDisabled = modeOption.mode === 2 && isGroupSigningDisabled;

                  return (
                    <div
                      key={modeOption.mode}
                      className="col-md-6"
                      data-toggle="tooltip"
                      data-placement="right"
                      title={isDisabled ? "Group Signing requires two or more recipients." : modeOption.tooltip}
                    >
                      <div
                        className={`option-card card h-100 border shadow-sm ${mode === modeOption.mode ? "active" : ""
                          } ${isDisabled ? "disabled-option" : ""}`}
                        onClick={() => !isDisabled && handleModeChange(modeOption.mode)}
                        style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                      >
                        <div className="card-body d-flex align-items-center gap-3">
                          <input
                            type="radio"
                            name="mode"
                            id={`mode${modeOption.mode}`}
                            value={modeOption.mode}
                            checked={mode === modeOption.mode}
                            onChange={(e) => !isDisabled && handleModeChange(parseInt(e.target.value))}
                            className="form-check-input"
                            disabled={isDisabled}
                          />
                          <div className="flex-grow-1">
                            <label
                              htmlFor={`mode${modeOption.mode}`}
                              className="fw-bold mb-1"
                              style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                            >
                              {modeOption.title}
                            </label>
                            <p className="text-muted small mb-0">
                              {modeOption.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Contract Details Section */}
            <div className="sleeve-sender-card mb-4">
              <h5 className="sleeve-heading mb-3">
                <span>
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Contract Information
                </span>
              </h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="docName" className="sleeve-step-label mb-2">Contract Title</label>
                  <input
                    type="text"
                    className="sleeve-step-input"
                    id="docName"
                    value={props.formObj.envelope_name_updated || props.formObj.envelope_name || ""}
                    disabled
                  />
                  {errors.doc_name && (
                    <div className="text-danger small mt-1">{errors.doc_name}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <div className="">
                    <CustomSelect
                      label="Contract Type"
                      name="contract_type"
                      value={contractType}
                      onChange={(e) => setContractType(e.target.value)}
                      required
                      error={errors.contract_type}
                      options={[
                        { value: "Non-Disclosure Agreement (NDA)", label: "Non-Disclosure Agreement (NDA)" },
                        { value: "Master Services Agreement (MSA)", label: "Master Services Agreement (MSA)" },
                      ]}
                      classNamePrefix="sleeve-sender-select"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <label htmlFor="docDetail" className="sleeve-step-label mb-2">Description</label>
                  <textarea
                    className={`sleeve-step-textarea ${errors.doc_detail ? "is-invalid" : ""
                      }`}
                    id="docDetail"
                    value={docDetail}
                    onChange={(e) => setDocDetail(e.target.value)}
                    placeholder="Enter a brief description of the contract..."
                    style={{ height: "120px" }}
                  ></textarea>

                  {errors.doc_detail && (
                    <div className="text-danger small mt-1">{errors.doc_detail}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Terms Section */}
            <div className="sleeve-sender-card mb-4">
              <h5 className="sleeve-heading mb-3">
                <span>
                  <i className="bi bi-file-text me-2"></i>
                  Contract Terms
                </span>
              </h5>
              <div className="row g-3">

                {/* Contract Term Toggle */}
                <div className="col-md-6">
                  <label className="sleeve-step-label mb-2">Contract Duration</label>
                  <div className="toggle-container ">
                    <div className="toggle-switch">
                      <span className={contractTerm === "Indefinite" ? "active" : ""}>
                        Indefinite
                      </span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={contractTerm === "Definite"}
                          onChange={(e) =>
                            setContractTerm(e.target.checked ? "Definite" : "Indefinite")
                          }
                        />
                        <span className="slider round"></span>
                      </label>
                      <span className={contractTerm === "Definite" ? "active" : ""}>
                        Definite
                      </span>
                    </div>
                  </div>
                  {errors.contract_term && (
                    <span className="text-danger small">{errors.contract_term}</span>
                  )}
                </div>

                {/* Renewable Toggle - Only for Definite contracts */}
                <div className="col-md-6">
                  {contractTerm === "Definite" && (
                    <>
                      <label className="sleeve-step-label mb-2">Is Contract Renewable</label>
                      <div className="toggle-container">
                        <div className="toggle-switch">
                          <span className={!isRenewable ? "active" : ""}>No</span>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={isRenewable}
                              onChange={(e) => setIsRenewable(e.target.checked)}
                            />
                            <span className="slider round"></span>
                          </label>
                          <span className={isRenewable ? "active" : ""}>Yes</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Start Date */}
                <div className="col-md-6">
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      startDateRef.current?.showPicker();
                    }}
                  >
                    <CustomDatePicker
                      label="Start Date"
                      required
                      selected={startDate ? new Date(startDate) : null}
                      onChange={(date) => setStartDate(date ? date.toISOString().split("T")[0] : "")}
                      error={errors.start_date}
                    />
                    {/* {errors.start_date && (
                      <div className="text-danger small mt-1">{errors.start_date}</div>
                    )} */}
                  </div>
                </div>

                {/* End Date */}
                <div className="col-md-6">
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      endDateRef.current?.showPicker();
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <CustomDatePicker
                      label="End Date"
                      selected={endDate ? new Date(endDate) : null}
                      onChange={(date) => setEndDate(date ? date.toISOString().split("T")[0] : "")}
                      minDate={startDate ? new Date(startDate) : null}
                      error={errors.end_date}
                    />
                    {/* {errors.end_date && (
                      <div className="text-danger small mt-1">{errors.end_date}</div>
                    )} */}
                  </div>
                </div>

                {/* Notice Period */}
                <div className="col-md-6">
                  <label htmlFor="noticePeriod" className="sleeve-step-label mb-2">Notice Period (days)</label>
                  <input
                    type="number"
                    className={`sleeve-step-input ${errors.termination_notice_period ? "is-invalid" : ""
                      }`}
                    id="noticePeriod"
                    value={terminationNoticePeriod}
                    onChange={(e) => setTerminationNoticePeriod(e.target.value)}
                    placeholder="Enter days"
                    min="0"
                  />

                  {errors.termination_notice_period && (
                    <div className="text-danger small mt-1">
                      {errors.termination_notice_period}
                    </div>
                  )}

                </div>
              </div>

              {/* NDA Specific Fields */}
              {contractType === "Non-Disclosure Agreement (NDA)" && (
                <div className="row g-3 mt-3">
                  <div className="col-md-6">
                    <label htmlFor="purpose" className="sleeve-step-label mb-2">Purpose of Disclosure*</label>
                    <input
                      type="text"
                      className={`sleeve-step-input ${errors.purpose_of_disclosure ? "is-invalid" : ""
                        }`}
                      id="purpose"
                      value={purposeOfDisclosure}
                      onChange={(e) => setPurposeOfDisclosure(e.target.value)}
                      placeholder="Purpose of Disclosure"
                    />

                    {errors.purpose_of_disclosure && (
                      <div className="text-danger small mt-1">
                        {errors.purpose_of_disclosure}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="durationMonths" className="sleeve-step-label mb-2" >Duration in Months*</label>
                    <input
                      type="number"
                      className={`sleeve-step-input ${errors.duration_in_months ? "is-invalid" : ""
                        }`}
                      id="durationMonths"
                      value={durationInMonths}
                      onChange={(e) => setDurationInMonths(e.target.value)}
                      placeholder="Duration in Months"
                      min="1"
                    />
                    {errors.duration_in_months && (
                      <div className="text-danger small mt-1">
                        {errors.duration_in_months}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="confidentialInfo" className="sleeve-step-label mb-2"> Confidential Info Definition*</label>
                    <textarea
                      className={`sleeve-step-textarea ${errors.confidential_info_definition ? "is-invalid" : ""
                        }`}
                      id="confidentialInfo"
                      value={confidentialInfoDefinition}
                      onChange={(e) => setConfidentialInfoDefinition(e.target.value)}
                      placeholder="Confidential Info Definition"
                      style={{ height: "100px" }}
                    ></textarea>

                    {errors.confidential_info_definition && (
                      <div className="text-danger small mt-1">
                        {errors.confidential_info_definition}
                      </div>
                    )}

                  </div>

                  <div className="col-md-6">
                    <CustomSelect
                      label="Exclusions"
                      name="exclusions"
                      value={exclusions}
                      onChange={(e) => setExclusions(e.target.value)}
                      required
                      error={errors.exclusions}
                      placeholder="Select Exclusion"
                      options={[
                        {
                          value: "Information already in the public domain",
                          label: "Information already in the public domain",
                        },
                        {
                          value: "Information independently developed",
                          label: "Information independently developed",
                        },
                      ]}
                      classNamePrefix="sleeve-sender-select"
                    />
                  </div>


                  <div className="col-md-6">
                    <label htmlFor="jurisdiction" className="sleeve-step-label mb-2">Jurisdiction*</label>
                    <input
                      type="text"
                      className={`sleeve-step-input ${errors.jurisdiction ? "is-invalid" : ""
                        }`}
                      id="jurisdiction"
                      value={jurisdiction}
                      onChange={(e) => setJurisdiction(e.target.value)}
                      placeholder="Jurisdiction"
                    />

                    {errors.jurisdiction && (
                      <div className="text-danger small mt-1">{errors.jurisdiction}</div>
                    )}

                  </div>
                </div>
              )}
            </div>

            {/* File Upload Section */}
            <div className="sleeve-sender-card mb-4">
              <h5 className="sleeve-heading mb-2">
                <span>
                  <i className="bi bi-cloud-arrow-up me-2"></i>
                  Document Upload{" "}
                  <i
                    className="bi bi-question-circle-fill ms-1 text-muted fs-6"
                    aria-hidden="true"
                    data-toggle="tooltip"
                    data-placement="right"
                    title="We'll merge your files in the order you upload them. At the end, we'll add a page with the contract details you entered."
                  />
                </span>
              </h5>
              <div className="p-2">
                {/* Upload Dropzone */}
                <div
                  className={`step2-empty-state ${isDragging ? "bg-white" : ""}`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    cursor: "pointer",
                    borderColor: isDragging ? "#0d6efd" : "#ced4da",
                    borderWidth: "2px"
                  }}
                >
                  <i className="bi bi-cloud-upload fs-1 text-primary mb-3"></i>
                  <p className="fw-bold mb-1">Drop files here or click to upload</p>
                  <small className="text-muted">PDF only, max 8MB</small>
                  <input
                    type="file"
                    ref={fileInputRef}
                    key={fileKey}
                    onChange={onDocFile}
                    accept="application/pdf"
                    className="d-none"
                    multiple
                  />
                </div>

                {/* Error */}
                {errors.doc_file && (
                  <div className="text-danger small mt-2">{errors.doc_file}</div>
                )}

                <p className="mt-3 text-muted small">
                  * You can reorder your uploaded documents by dragging
                </p>

                {/* Uploaded Files List */}
                {documentsList.length > 0 && (
                  <div className="uploaded-files mt-3">
                    {documentsList.map((item, index) => (
                      <div
                        key={index}
                        className="file-item d-flex align-items-center justify-content-between border rounded-3 p-3 mb-2 bg-light shadow-sm"
                        draggable
                        onDragStart={(e) => handleDocDragStart(e, index)}
                        onDragEnter={(e) => handleDocDragEnter(e, index)}
                        onDragEnd={handleDocDrop}
                        onDragOver={(e) => e.preventDefault()}
                        style={{ cursor: "grab" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="file-index badge bg-primary"
                            style={{
                              borderRadius: "50%",
                              height: "30px",
                              width: "30px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {index + 1}
                          </span>
                          <span className="file-name fw-semibold">{item.name}</span>
                          {!item.file && (
                            <span className="badge bg-secondary ms-2">Existing</span>
                          )}
                        </div>
                        <div className="file-actions d-flex align-items-center gap-3">
                          {item.path && (
                            <i
                              className="bi bi-download text-success fs-5"
                              title="Download"
                              onClick={(e) => handleDownload(e, item)}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                          <i
                            className="bi bi-x text-danger fs-5"
                            title="Remove"
                            onClick={(event) => onRemoveDoc(event, item)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="sleeve-step-buttons">
              <button className="sleeve-btn-back" onClick={onBack}>
                Back
              </button>
              <div className="sleeve-step-buttons-right">
                <button className="sleeve-btn-back me-2" onClick={clearDocumentForm}>
                  Cancel
                </button>
                <button className="sleeve-btn-next"
                  onClick={onSaveDoc}
                  disabled={props.loading}
                >
                  {props.loading ? "Saving..." : "Save "}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {showForm ? (
        props.is_negotiation ? (
          renderNegotiationForm()
        ) : (
          renderFullContractForm()
        )
      ) : (
        <div className="sleeve-gradient-bg sleeve-step">
          <div className="sleeve-main-card">
            {/* Custom Header */}
            <div className="sleeve-contract-setup-header">
              <div className="sleeve-contract-setup-header-content">
                <div>
                  <div className="sleeve-contract-title">Contract Documents</div>
                  <div className="sleeve-contract-subtitle">Manage your added contracts and documents.</div>
                </div>
                <div className="text-end">
                  {!props.is_negotiation && <div className="sleeve-step-indicator-custom">Step 3 of 4</div>}
                </div>
              </div>
            </div>

            <div className="sleeve-sender-card mb-4">
              <div className="col-lg-12 px-0">
                {props.formObj.sign_documents.length === 0 ? (
                  <div className="text-center py-5">
                    <i
                      className="bi bi-inbox text-muted"
                      style={{ fontSize: "3rem" }}
                    ></i>
                    <h6 className="mb-1 fs-4 fw-bold mt-3">
                      No Contract requests available
                    </h6>
                    <p className="text-muted my-3">
                      Add a new contract to get started.
                    </p>
                    <button className="sleeve-btn-save mt-2" onClick={() => setShowForm(true)}>
                      + Add Contract
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="sleeve-heading mb-0">Added Contracts</h5>
                       
                    </div>
                    {props.formObj.sign_documents.map((item, index) => (
                      <div key={index} className="rounded dt-alert-info mb-2 px-3 py-3 shadow-sm border-0">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <span className="text-white fw-semibold fs-5">
                              {Utils.getCleanEnvelopeName(item.name, props.formObj?.is_negotiation)}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            {item.request_display.length === 0 ? (
                              <span
                                onClick={(e) => onRequestSignDocumentRender(e, item)}
                                className="btn btn-sm btn-light py-1 px-3 fw-bold text-primary"
                                style={{ cursor: "pointer" }}
                              >
                                Make Fillable
                              </span>
                            ) : (
                              <span
                                onClick={(e) => onRequestSignDocumentRender(e, item)}
                                className="btn btn-sm btn-light py-1 px-3 fw-bold text-primary"
                                style={{ cursor: "pointer" }}
                              >
                                Edit Live Form
                              </span>
                            )}
                           {/* <ContractSummaryModal
                              envelopeId={props.formObj.envelope_id}
                              fileName={item.file_name}
                              renderTrigger={({ onClick, isLoading }) => (
                                <button className="btn btn-sm btn-light py-1 px-3 fw-bold text-primary" style={{ cursor: "pointer" }} onClick={onClick}>
                                  Summary
                                </button>
                              )}
                            /> */}
                            <i
                              className="bi bi-pencil text-white"
                              onClick={(e) => onDocumentEdit(e, item)}
                              style={{ cursor: "pointer", fontSize: "1.2rem" }}
                              title="Edit"
                            />
                            <i
                              className="bi bi-download text-white"
                              onClick={(e) => handleDownload(e, item)}
                              style={{ cursor: "pointer", fontSize: "1.2rem" }}
                              title="Download"
                            />
                            {/* <i
                              onClick={(e) => onRequestSignDocumentDelete(e, item)}
                              className="bi bi-trash text-white"
                              style={{ cursor: "pointer", fontSize: "1.2rem" }}
                              title="Delete"
                            /> */}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                <span
                  className="d-none"
                  ref={btnSignDocumentPopup}
                  data-bs-toggle="offcanvas"
                  data-bs-target="#makeFillableForm"
                ></span>
              </div>
            </div>

            <div className="sleeve-step-buttons">
              <button className="sleeve-btn-back" onClick={onBack}>
                Back
              </button>
              <div className="sleeve-step-buttons-right">
                <button onClick={onSaveExit} className="sleeve-btn-save">
                  Save & Exit
                </button>
                <button className="sleeve-btn-next" onClick={onSave}>
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <MakeFillablePopup
        formObj={props.formObj}
        signatureDocumentData={signatureDocumentData}
        setFormObj={props.setFormObj}
        setLoading={props.setLoading}
        makeFillable={makeFillable}
        handleMakeFillable={handleMakeFillable}
      />
    </>
  );
};
export default EnvelopeStep3;
